import { useCallback, useRef } from 'react';

interface UseInflightWritesReturn {
	/** Registers a write so `settled()` waits for it. Returns the same promise. */
	track: <T>(promise: Promise<T>) => Promise<T>;
	/** Resolves once every tracked write has settled (rejections included), so a
	 * failed write can't leave a caller waiting forever. */
	settled: () => Promise<void>;
}

/**
 * Waiting room for fire-and-forget writes (e.g. useMutation.mutate) that a later
 * action must not overtake. Callers gate on `settled()` instead of disabling the
 * trigger while a write is in flight, which would swallow clicks.
 */
export function useInflightWrites(): UseInflightWritesReturn {
	const inflightRef = useRef(new Set<Promise<unknown>>());

	const track = useCallback(<T>(promise: Promise<T>): Promise<T> => {
		const settledPromise = promise.then(
			() => undefined,
			() => undefined
		);
		inflightRef.current.add(settledPromise);
		void settledPromise.then(() => {
			inflightRef.current.delete(settledPromise);
		});
		return promise;
	}, []);

	const settled = useCallback(async () => {
		// Re-check after each drain: a write tracked while we were waiting (a
		// follow-up POST, a retry) still has to be covered.
		while (inflightRef.current.size > 0) {
			await Promise.all([...inflightRef.current]);
		}
	}, []);

	return { track, settled };
}
